import { useEffect, useMemo } from 'react'
import { collection, collectionGroup, documentId, onSnapshot, query, where } from 'firebase/firestore'
import { useQueryClient } from '@tanstack/react-query'
import { db } from '../../../libs/Firebase'
import { listenWithFreshClaims } from '../../../apiLayer/handlers/directDbHandler'
import { CollectionGroups, Collections } from '../../../../../pure-js/libs/Collections'
import { GAMER_ACTIVITY_QUERY_KEYS } from '../../shared/constants/activitySync'
import useAppState from '../../../hooks/useAppState'

// Firestore 'in' supports at most 30 values.
const FIRESTORE_IN_LIMIT = 30

/**
 * Attaches a Firestore real-time listener to all session documents for the
 * gamer's teams. When any session changes (game started, ended, status updated),
 * invalidates the React Query cache so the UI re-fetches via the existing
 * Cloud Function hooks.
 *
 * Safe to mount once in GamerDashboard — listener is cleaned up on unmount.
 */
export const useGamerActivitiesRealtimeSync = (): void => {
  const queryClient = useQueryClient()
  const { state } = useAppState()
  // Stable references so the listener effects don't re-subscribe on every render.
  const teamIds = useMemo(() => state.user?.teamIds ?? [], [state.user?.teamIds])
  const sprintIds = useMemo(() => state.user?.sprintIds ?? [], [state.user?.sprintIds])

  // Game completions write a session doc → watch the team's sessions.
  useEffect(() => {
    // Firestore 'in' query requires a non-empty array
    if (teamIds.length === 0) return

    const sessionsQuery = query(collectionGroup(db, CollectionGroups.SESSIONS), where('teamId', 'in', teamIds))
    const invalidateAll = () => {
      GAMER_ACTIVITY_QUERY_KEYS.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] })
      })
    }

    // A team joined after sign-in is missing from the token until refreshed; the wrapper retries a denied read on a fresh one.
    return listenWithFreshClaims(
      (onListenError) => onSnapshot(sessionsQuery, invalidateAll, onListenError),
      (error) => console.error('gamer sessions listener failed', error)
    )
  }, [teamIds, queryClient])

  // Timed activities (intro / AI-coach debrief) have NO session — their completion is written to the SPRINT
  // doc, which the session listener above can't see. Watch the user's sprints too, so the dashboard's
  // completion %/"Completed" updates without a manual refresh (matches how game completions already do).
  useEffect(() => {
    if (sprintIds.length === 0) return

    const sprintsQuery = query(
      collection(db, Collections.SPRINTS),
      where(documentId(), 'in', sprintIds.slice(0, FIRESTORE_IN_LIMIT))
    )

    const invalidateAll = () => {
      GAMER_ACTIVITY_QUERY_KEYS.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] })
      })
    }

    return listenWithFreshClaims(
      (onListenError) => onSnapshot(sprintsQuery, invalidateAll, onListenError),
      () => {
        // Ignore transient listener errors — the 30s staleTime refetch still keeps data fresh.
      }
    )
  }, [sprintIds, queryClient])
}
